import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./PendingVerifications.css";
import "./NewLeaveByOffice.css";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const MAX_DOC_KB = 200;

const getToday = () => new Date().toISOString().slice(0, 10);

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-GB");
}

function aTypeLabel(val) {
  if (val === "N") return "National";
  if (val === "I") return "International";
  return val || "";
}

const calcNoOfDays = (fromDate, toDate) => {
  if (!fromDate || !toDate) return "";
  const start = new Date(fromDate);
  const end   = new Date(toDate);
  if (isNaN(start) || isNaN(end) || start > end) return "";
  return String(Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1);
};

export default function NewAchievementOffice() {
  const navigate = useNavigate();

  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);

  const [employees,   setEmployees]   = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [loadingInit, setLoadingInit] = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState("");
  const [success,     setSuccess]     = useState("");

  const [form, setForm] = useState({
    EmployeeID:  "",
    AchiCatID:   "",
    Title:       "",
    AType:       "N",
    FromDate:    getToday(),
    ToDate:      "",
    NoOfDays:    "",
    OrderDate:   "",
    OrderNumber: "",
    Remarks:     "",
    SupportDoc:  null,
  });

  const [employeeAchievements, setEmployeeAchievements] = useState([]);
  const [loadingAchievements,  setLoadingAchievements]  = useState(false);

  useEffect(() => {
    if (moduleId !== 4) return;
    const load = async () => {
      setLoadingInit(true);
      try {
        const empParams = new URLSearchParams({ moduleId });
        const [empRes, catRes] = await Promise.all([
          fetch(`${API_ROOT}/api/employees/leave-employees-office?${empParams}`),
          fetch(`${API_ROOT}/api/employees/achievement-categories?moduleId=${moduleId}`),
        ]);
        const empData = await empRes.json();
        const catData = await catRes.json();
        setEmployees(Array.isArray(empData.data) ? empData.data : []);
        setCategories(Array.isArray(catData)      ? catData      : []);
      } catch {
        setError("Failed to load form data.");
      } finally {
        setLoadingInit(false);
      }
    };
    load();
  }, [moduleId]);

  // Display all existing achievements of the selected employee as soon as they're picked.
  useEffect(() => {
    if (moduleId !== 4 || !form.EmployeeID) {
      setEmployeeAchievements([]);
      return;
    }
    const fetchAchievements = async () => {
      setLoadingAchievements(true);
      try {
        const params = new URLSearchParams({ moduleId, empId: form.EmployeeID });
        const res  = await fetch(`${API_ROOT}/api/employees/achievement-details?${params}`);
        const data = await res.json();
        setEmployeeAchievements(res.ok && Array.isArray(data.data) ? data.data : []);
      } catch {
        setEmployeeAchievements([]);
      } finally {
        setLoadingAchievements(false);
      }
    };
    fetchAchievements();
  }, [form.EmployeeID, moduleId]);

  useEffect(() => {
    setForm(prev => ({ ...prev, NoOfDays: calcNoOfDays(prev.FromDate, prev.ToDate) }));
  }, [form.FromDate, form.ToDate]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setForm(prev => ({ ...prev, SupportDoc: null }));
      return;
    }
    const allowed = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    if (!allowed.includes(file.type)) {
      setError("Only PDF, JPG, JPEG, PNG files are allowed.");
      e.target.value = "";
      setForm(prev => ({ ...prev, SupportDoc: null }));
      return;
    }
    if (file.size > MAX_DOC_KB * 1024) {
      setError(`Supporting document must be less than ${MAX_DOC_KB} KB`);
      e.target.value = "";
      setForm(prev => ({ ...prev, SupportDoc: null }));
      return;
    }
    setError("");
    setForm(prev => ({ ...prev, SupportDoc: file }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.EmployeeID || !form.AchiCatID || !form.Title || !form.FromDate || !form.ToDate) {
      setError("All required fields must be filled.");
      return;
    }
    if (new Date(form.FromDate) > new Date(form.ToDate)) {
      setError("From Date cannot be after To Date.");
      return;
    }

    const payload = new FormData();
    payload.append("moduleId",   String(moduleId));
    payload.append("EmployeeID", form.EmployeeID);
    payload.append("AchiCatID",  form.AchiCatID);
    payload.append("Title",      form.Title);
    payload.append("AType",      form.AType);
    payload.append("FromDate",   form.FromDate);
    payload.append("ToDate",     form.ToDate);
    payload.append("OrderDate",   form.OrderDate || "");
    payload.append("OrderNumber", form.OrderNumber || "");
    payload.append("Remarks",    form.Remarks || "");
    if (form.SupportDoc) payload.append("SupportDoc", form.SupportDoc);

    try {
      setSubmitting(true);
      const res  = await fetch(`${API_ROOT}/api/employees/achievement-new`, {
        method: "POST",
        body:   payload,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to submit");

      setSuccess(data.message || "Achievement added successfully.");
      const employeeId = form.EmployeeID;
      setForm({
        EmployeeID:  employeeId,
        AchiCatID:   "",
        Title:       "",
        AType:       "N",
        FromDate:    getToday(),
        ToDate:      "",
        NoOfDays:    "",
        OrderDate:   "",
        OrderNumber: "",
        Remarks:     "",
        SupportDoc:  null,
      });
    } catch (err) {
      setError(err.message || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (moduleId !== 4) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <div className="d-flex align-items-center mb-3 gap-2">
        <h3 className="ctitle mb-0">New Achievement</h3>
        <button
          className="btn btn-secondary btn-sm ms-3"
          onClick={() => navigate("/achievements-office")}
        >
          Achievement Details
        </button>
      </div>

      {error   && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={onSubmit}>
        <div className="row g-2">

          <div className="col-sm-4">
            <label className="form-label mb-1">Employee <span className="text-danger">*</span></label>
            <select
              className="form-select"
              name="EmployeeID"
              value={form.EmployeeID}
              onChange={onChange}
              disabled={loadingInit}
              required
            >
              <option value="">-- Select Employee --</option>
              {employees.map(e => (
                <option key={e.EmployeeID} value={e.EmployeeID}>
                  {[e.EmpName, e.Designation, e.Department, e.Mobile].filter(Boolean).join(" - ")}
                </option>
              ))}
            </select>
          </div>

          <div className="col-auto" style={{ width: "180px" }}>
            <label className="form-label mb-1">Category <span className="text-danger">*</span></label>
            <select
              className="form-select"
              name="AchiCatID"
              value={form.AchiCatID}
              onChange={onChange}
              disabled={loadingInit}
              required
            >
              <option value="">-- Select Category --</option>
              {categories.map(c => (
                <option key={c.AchiCatID} value={c.AchiCatID}>{c.AchiCategory}</option>
              ))}
            </select>
          </div>

          <div className="col-auto" style={{ width: "140px" }}>
            <label className="form-label mb-1">Type <span className="text-danger">*</span></label>
            <select className="form-select" name="AType" value={form.AType} onChange={onChange}>
              <option value="N">National</option>
              <option value="I">International</option>
            </select>
          </div>

          <div className="col-auto nlbo-date">
            <label className="form-label mb-1">From Date <span className="text-danger">*</span></label>
            <input className="form-control" type="date" name="FromDate" value={form.FromDate} onChange={onChange} required />
          </div>

          <div className="col-auto nlbo-date">
            <label className="form-label mb-1">To Date <span className="text-danger">*</span></label>
            <input className="form-control" type="date" name="ToDate" value={form.ToDate} onChange={onChange} required />
          </div>

          <div className="col-sm-2 nlbo-days">
            <label className="form-label mb-1">No of Days</label>
            <input className="form-control" type="text" value={form.NoOfDays} readOnly placeholder="Auto" />
          </div>

          <div className="col-sm-6">
            <label className="form-label mb-1">Title <span className="text-danger">*</span></label>
            <input
              className="form-control"
              type="text"
              name="Title"
              value={form.Title}
              onChange={onChange}
              maxLength={150}
              required
            />
          </div>

          <div className="col-auto nlbo-date">
            <label className="form-label mb-1">Order Date</label>
            <input className="form-control" type="date" name="OrderDate" value={form.OrderDate} onChange={onChange} />
          </div>

          <div className="col-auto" style={{ width: "160px" }}>
            <label className="form-label mb-1">Order Number</label>
            <input
              className="form-control"
              type="text"
              name="OrderNumber"
              value={form.OrderNumber}
              onChange={onChange}
              maxLength={50}
            />
          </div>

          <div className="col-sm-3">
            <label className="form-label mb-1">
              Supporting Document (200 KB){" "}
              <i className="bi bi-info-circle" title="PDF, JPG, JPEG, PNG — max 200 KB" />
            </label>
            <input className="form-control" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={onFileChange} />
          </div>

          <div className="col-auto" style={{ width: "220px" }}>
            <label className="form-label mb-1">Remarks</label>
            <input
              className="form-control"
              type="text"
              name="Remarks"
              value={form.Remarks}
              onChange={onChange}
              maxLength={255}
              placeholder="Remarks..."
            />
          </div>

          <div className="col-12 mt-2">
            <button type="submit" className="btn btn-primary" disabled={submitting || loadingInit}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>

        </div>
      </form>

      {form.EmployeeID && (
        <div className="mt-4">
          <h5 className="ctitle">Achievement History</h5>
          {loadingAchievements ? (
            <div className="text-muted">Loading...</div>
          ) : employeeAchievements.length === 0 ? (
            <div className="alert alert-info">No achievement records found for this employee.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle pending-verif-table">
                <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
                  <tr>
                    <th>SNo</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Days</th>
                    <th>Order Date</th>
                    <th>Order No.</th>
                    <th className="remarks-cell">Remarks</th>
                    <th>Attachment</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeAchievements.map((row, idx) => (
                    <tr key={row.AchievementID ?? idx}>
                      <td>{idx + 1}</td>
                      <td>{row.Title}</td>
                      <td>{row.AchiCategory}</td>
                      <td>{aTypeLabel(row.AType)}</td>
                      <td>{formatDate(row.FromDate)}</td>
                      <td>{formatDate(row.ToDate)}</td>
                      <td>{row.NoOfDays}</td>
                      <td>{formatDate(row.OrderDate)}</td>
                      <td>{row.OrderNumber}</td>
                      <td className="remarks-cell">{row.Remarks}</td>
                      <td>
                        {row.SupportDoc ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => window.open(`${API_ROOT}/api/${row.SupportDoc}`, "_blank", "noopener,noreferrer")}
                          >
                            View
                          </button>
                        ) : <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
